// 設定 → 安全性 → 登入中的設備（plan/vms-web-v0.md AUTH-06）：強制登出後 30 秒內該工作階段的即時影像會停。
import { useTranslation } from 'react-i18next';
import { useConfirm } from '../../components/ConfirmDialog';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { listSessions, revokeAllSessions, revokeSession, SessionError, type SessionInfo } from '../../lib/session';
import { stamp } from '../../lib/timefmt';

export function clientLabel(t: (k: string, o?: Record<string, unknown>) => string, s: Pick<SessionInfo, 'client_id' | 'device_label'>): string {
  const app = t(`sessions.client_${s.client_id.replace(/-/g, '_')}`, { defaultValue: s.client_id });
  return s.device_label ? `${s.device_label} · ${app}` : app;
}

export function SessionsSection() {
  const { t } = useTranslation();
  const { ask, dialog } = useConfirm();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['sessions'], queryFn: () => listSessions() });
  const refresh = () => void qc.invalidateQueries({ queryKey: ['sessions'] });
  const one = useMutation({ mutationFn: (id: string) => revokeSession(id), onSuccess: refresh });
  const others = useMutation({ mutationFn: () => revokeAllSessions(true), onSuccess: refresh });
  const err = (one.error ?? others.error) as unknown;
  const list = q.data ?? [];
  return (
    <section className="card">
      <div className="toolbar">
        <h2 className="section-title">{t('sessions.title')}</h2>
        <div className="spacer" />
        {list.filter((s) => !s.current).length > 0 && (
          <button
            className="btn btn-sm"
            disabled={others.isPending}
            onClick={() => {
              void ask({ danger: true, title: t('sessions.revoke_others_confirm'), action: t('common.action_sign_out') }).then((ok) => ok && others.mutate());
            }}
          >
            {t('sessions.revoke_others')}
          </button>
        )}
      </div>
      <p className="muted small">{t('sessions.hint')}</p>
      {q.isLoading && <p className="muted">{t('common.loading')}</p>}
      {err != null && <p className="error">{err instanceof SessionError ? err.code : t('common.error')}</p>}
      <div className="table-wrap">
        <table className="grid">
          <thead>
            <tr>
              <th>{t('sessions.device')}</th>
              <th>{t('sessions.ip')}</th>
              <th>{t('sessions.signed_in')}</th>
              <th>{t('sessions.last_active')}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {list.map((s) => (
              <tr key={s.session_id}>
                <td>
                  {clientLabel(t, s)} {s.current && <span className="badge badge-ok">{t('sessions.current')}</span>}
                  {s.amr.includes('hwk') ? (
                    <span className="muted small"> · passkey</span>
                  ) : (
                    (s.amr.includes('otp') || s.amr.includes('recovery')) && <span className="muted small"> · {t('sessions.mfa')}</span>
                  )}
                </td>
                <td className="mono small">{s.last_ip || '—'}</td>
                <td className="small muted">{s.auth_time ? stamp(s.auth_time) : '—'}</td>
                <td className="small muted">{stamp(s.last_active_at)}</td>
                <td>
                  <button
                    className="btn btn-sm"
                    disabled={one.isPending}
                    onClick={() => {
                      if (!s.current) one.mutate(s.session_id);
                      else void ask({ title: t('sessions.revoke_current_confirm'), action: t('common.action_sign_out') }).then((ok) => ok && one.mutate(s.session_id));
                    }}
                  >
                    {s.current ? t('sessions.sign_out') : t('sessions.revoke')}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {dialog}
    </section>
  );
}
